import {
  AlertTriangle,
  BadgeCheck,
  BriefcaseIcon,
  Building2,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  CalendarRange,
  Euro,
  FilePen,
  FileUp,
  Globe,
  Hash,
  Mail,
  MapPin,
  PartyPopper,
  Percent,
  Phone,
  Search,
  Shield,
  ShieldAlert,
  User,
} from 'lucide-react';
import type {
  ConditionalVisibility,
  FormSection,
  FormSectionsWithShortcuts,
  KorintModulesProductsConfig,
} from 'korint-sdk/react';
import { ContactKey } from '@folio/utils/customerDisplayName';
import {
  ManualQuoteComponentDto,
  ManualQuotePercentageDto,
  policyControllerPushPolicyEvent,
  PolicyDto,
} from 'korint-sdk';
import { centsToEuros, eurosToCents } from '@folio/utils';
import z, { ZodTypeAny } from 'zod';

const showWhenCompanyExists = [
  {
    fieldName: 'isCompanyBeingCreated',
    condition: 'equals',
    value: 'false',
  },
] as const satisfies ConditionalVisibility[];

const showWhenCompanyBeingCreated = [
  {
    fieldName: 'isCompanyBeingCreated',
    condition: 'equals',
    value: 'true',
  },
] as const satisfies ConditionalVisibility[];

// Killing questions visibility by activity product
const showForTECH = [
  {
    apiProperty: 'customer.customFields.companyActivityProduct',
    condition: 'equals',
    value: 'TECH',
  },
] as const satisfies ConditionalVisibility[];

const showForMAC = [
  {
    apiProperty: 'customer.customFields.companyActivityProduct',
    condition: 'equals',
    value: 'MAC',
  },
] as const satisfies ConditionalVisibility[];

const showForMED = [
  {
    apiProperty: 'customer.customFields.companyActivityProduct',
    condition: 'equals',
    value: 'MED',
  },
] as const satisfies ConditionalVisibility[];

const showForMAN = [
  {
    apiProperty: 'customer.customFields.companyActivityProduct',
    condition: 'equals',
    value: 'MAN',
  },
] as const satisfies ConditionalVisibility[];

const showForMISC = [
  {
    apiProperty: 'customer.customFields.companyActivityProduct',
    condition: 'equals',
    value: 'MISC',
  },
] as const satisfies ConditionalVisibility[];

const showForTTP = [
  {
    apiProperty: 'customer.customFields.companyActivityProduct',
    condition: 'equals',
    value: 'TTP',
  },
] as const satisfies ConditionalVisibility[];

const showForBIE = [
  {
    apiProperty: 'customer.customFields.companyActivityProduct',
    condition: 'equals',
    value: 'BIE',
  },
] as const satisfies ConditionalVisibility[];

const showForTAG = [
  {
    apiProperty: 'customer.customFields.companyActivityProduct',
    condition: 'equals',
    value: 'TAG',
  },
] as const satisfies ConditionalVisibility[];

const showForSECU = [
  {
    apiProperty: 'customer.customFields.companyActivityProduct',
    condition: 'equals',
    value: 'SECU',
  },
] as const satisfies ConditionalVisibility[];

const showForBET = [
  {
    apiProperty: 'customer.customFields.companyActivity1',
    condition: 'equals',
    value: "Bureau d'études techniques",
  },
] as const satisfies ConditionalVisibility[];

const showForACE = [
  {
    apiProperty: 'customer.customFields.companyActivityProduct',
    condition: 'equals',
    value: 'ACE',
  },
] as const satisfies ConditionalVisibility[];

const showForWholeWorldEligible = [
  {
    apiProperty: 'customer.customFields.companyActivityProduct',
    condition: 'in',
    values: ['TECH', 'MAC', 'MED', 'MAN', 'TAG'],
  },
] as const satisfies ConditionalVisibility[];

const killingQuestionBlockingValidation = {
  additionalValidationRules: (schema: ZodTypeAny) =>
    schema.and(
      z.literal('false', {
        errorMap: () => ({
          message: 'Il est impossible de souscrire une police dans ce cas',
        }),
      }),
    ),
};

const BILLING_FREQUENCY_ICONS = {
  MONTHLY: CalendarDays,
  QUARTERLY: CalendarRange,
  HALF_YEARLY: CalendarClock,
  YEARLY: CalendarCheck,
};

const killingQuestionField = (name: string) => ({
  name,
  ...killingQuestionBlockingValidation,
  display: 'toggle' as const,
  blockingValue: 'true',
  blockingErrorMessage: 'Il est impossible de souscrire une police dans ce cas',
  icon: AlertTriangle,
});

const policySections: FormSectionsWithShortcuts = [
  {
    title: 'Questions de souscription - Conditions générales',
    fields: [
      killingQuestionField('kqHiscoxHistory'),
      killingQuestionField('kqGeographicExposure'),
      killingQuestionField('kqClaimsHistory'),
    ],
  },
  {
    title: 'Questions de souscription - Secteur Tech',
    showWhen: showForTECH,
    fields: [killingQuestionField('kqActivityTECH'), killingQuestionField('kqPayrollPortage')],
  },
  {
    title: 'Questions de souscription - Marketing & Communication',
    showWhen: showForMAC,
    fields: [killingQuestionField('kqActivityMAC'), killingQuestionField('kqPayrollPortage')],
  },
  {
    title: 'Questions de souscription - Médias',
    showWhen: showForMED,
    fields: [killingQuestionField('kqActivityMED'), killingQuestionField('kqPayrollPortage')],
  },
  {
    title: 'Questions de souscription - Management & Conseil',
    showWhen: showForMAN,
    fields: [killingQuestionField('kqActivityMAN'), killingQuestionField('kqPayrollPortage')],
  },
  {
    title: 'Questions de souscription - Activités diverses',
    showWhen: showForMISC,
    fields: [killingQuestionField('kqActivityMISC'), killingQuestionField('kqPayrollPortage')],
  },
  {
    title: 'Questions de souscription - Tatouage et Perçage',
    showWhen: showForTTP,
    fields: [killingQuestionField('kqActivityTTP'), killingQuestionField('kqPayrollPortage')],
  },
  {
    title: 'Questions de souscription - Bien-être',
    showWhen: showForBIE,
    fields: [killingQuestionField('kqActivityBIE'), killingQuestionField('kqPayrollPortage')],
  },
  {
    title: 'Questions de souscription - Tourisme et Voyages',
    showWhen: showForTAG,
    fields: [killingQuestionField('kqActivityTAG'), killingQuestionField('kqPayrollPortage')],
  },
  {
    title: 'Questions de souscription - Sécurité',
    showWhen: showForSECU,
    fields: [killingQuestionField('kqActivitySECU')],
  },
  {
    title: "Questions de souscription - Bureau d'Études Techniques",
    showWhen: showForBET,
    fields: [killingQuestionField('kqActivityBET'), killingQuestionField('kqPayrollPortage')],
  },
  {
    title: 'Questions de souscription - Artisanat, Commerce et Entreprise',
    showWhen: showForACE,
    fields: [killingQuestionField('kqActivityACE'), killingQuestionField('kqPayrollPortage')],
  },
  {
    title: 'Questions de souscription - Extension Monde entier',
    showWhen: showForWholeWorldEligible,
    fields: ['kqUSACanadaExposure'],
  },
];

const signatorySections: FormSectionsWithShortcuts = [
  {
    title: 'Signataire',
    fields: [
      {
        name: 'contactCivility',
        display: 'small-cards' as const,
        icons: { MONSIEUR: User, MADAME: User, MADEMOISELLE: User },
      },
      { name: 'contactFirstName', colSpan: 6, icon: User },
      { name: 'contactLastName', colSpan: 6, icon: User },
      {
        name: 'contactEmail',
        colSpan: 6,
        icon: Mail,
      },
      {
        name: 'contactPhoneNumber',
        colSpan: 6,
        icon: Phone,
      },
      { name: 'contactBirthDate', colSpan: 4, icon: CalendarDays },
      {
        name: 'contactBirthCity',
        type: 'city',
        addressGroup: 'contactBirth',
        colSpan: 4,
        icon: Building2,
      },
      {
        name: 'contactBirthCountry',
        type: 'country',
        addressGroup: 'contactBirth',
        colSpan: 4,
        icon: Globe,
      },
    ],
  },
];

const companySections: FormSectionsWithShortcuts = [
  {
    title: "Informations de l'entreprise",
    fields: [
      { name: 'isCompanyBeingCreated', display: 'toggle' as const, icon: Building2 },
      {
        name: 'companySiret',
        type: 'company',
        isCustomField: true,
        displayCompanyInformation: true,
        icon: Search,
        apiDataAccessor: apiData =>
          apiData.customFields?.companySiret
            ? {
                siret: apiData?.customFields?.companySiret,
                name: apiData?.customFields?.companyName,
                location: {
                  postCode: apiData?.customFields?.companyHeadquarterPostcode,
                  city: apiData?.customFields?.companyHeadquarterCity,
                  address: apiData?.customFields?.companyHeadquarterAddress,
                },
                nafCode: apiData?.customFields?.companyNafCode,
              }
            : null,
        formDataAccessor: 'companySiret.siret',
        showWhen: showWhenCompanyExists,
      },
      {
        name: 'companyName',
        icon: Building2,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyLegalStatus1',
        icon: Hash,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyCreationDate',
        colSpan: 6,
        icon: CalendarDays,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyNafCode',
        colSpan: 6,
        icon: Hash,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyHeadquarterAddress',
        type: 'address',
        icon: MapPin,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyHeadquarterPostcode',
        colSpan: 4,
        icon: Hash,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyHeadquarterCity',
        type: 'city',
        colSpan: 8,
        icon: Building2,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyHeadquarterCountry',
        type: 'country',
        icon: Globe,
        showWhen: showWhenCompanyBeingCreated,
      },
    ],
  },
  {
    title: 'Détail des activités',
    fields: [
      { name: 'companyActivity1', searchable: true, icon: BriefcaseIcon, colSpan: 8 },
      {
        name: 'companyActivityDistribution1',
        icon: Percent,
        colSpan: 4,
      },
      {
        name: 'companyActivity2',
        type: 'hiscoxActivity' as const,
        icon: BriefcaseIcon,
        colSpan: 8,
        showWhen: [
          {
            fieldName: 'companyActivity1',
            condition: 'exists',
          },
        ],
      },
      {
        name: 'companyActivityDistribution2',
        icon: Percent,
        colSpan: 4,
        showWhen: [
          {
            fieldName: 'companyActivity2',
            condition: 'exists',
          },
        ],
      },
      {
        name: 'companyActivity3',
        type: 'hiscoxActivity' as const,
        icon: BriefcaseIcon,
        colSpan: 8,
        showWhen: [
          {
            fieldName: 'companyActivity2',
            condition: 'exists',
          },
        ],
      },
      {
        name: 'companyActivityDistribution3',
        icon: Percent,
        colSpan: 4,
        showWhen: [
          {
            fieldName: 'companyActivity3',
            condition: 'exists',
          },
        ],
      },
      {
        name: 'companyActivity4',
        type: 'hiscoxActivity' as const,
        icon: BriefcaseIcon,
        colSpan: 8,
        showWhen: [
          {
            fieldName: 'companyActivity3',
            condition: 'exists',
          },
        ],
      },
      {
        name: 'companyActivityDistribution4',
        icon: Percent,
        colSpan: 4,
        showWhen: [
          {
            fieldName: 'companyActivity4',
            condition: 'exists',
          },
        ],
      },
      {
        name: 'companyActivityDistributionTotal',
        type: 'computed',
        hidden: true,
        ignoreInPayload: true,
        apiDataAccessor: (data: {
          companyActivityDistribution1?: number;
          companyActivityDistribution2?: number;
          companyActivityDistribution3?: number;
          companyActivityDistribution4?: number;
        }) => {
          const {
            companyActivityDistribution1 = 0,
            companyActivityDistribution2 = 0,
            companyActivityDistribution3 = 0,
            companyActivityDistribution4 = 0,
          } = data as {
            companyActivityDistribution1?: number;
            companyActivityDistribution2?: number;
            companyActivityDistribution3?: number;
            companyActivityDistribution4?: number;
          };
          return (
            companyActivityDistribution1 +
            companyActivityDistribution2 +
            companyActivityDistribution3 +
            companyActivityDistribution4
          );
        },
        formDataAccessor: (data: Record<string, unknown>) => {
          const {
            companyActivityDistribution1 = 0,
            companyActivityDistribution2 = 0,
            companyActivityDistribution3 = 0,
            companyActivityDistribution4 = 0,
          } = data as {
            companyActivityDistribution1?: number | string;
            companyActivityDistribution2?: number | string;
            companyActivityDistribution3?: number | string;
            companyActivityDistribution4?: number | string;
          };
          return (
            Number(companyActivityDistribution1) +
            Number(companyActivityDistribution2) +
            Number(companyActivityDistribution3) +
            Number(companyActivityDistribution4)
          );
        },
        additionalValidationRules: (schema: ZodTypeAny) =>
          schema.and(
            z.number().refine((data: number) => data === 100, {
              message: "La somme des répartitions d'activités doit être égale à 100%",
            }),
          ),
        dependsOn: [
          'companyActivityDistribution1',
          'companyActivityDistribution2',
          'companyActivityDistribution3',
          'companyActivityDistribution4',
        ],
      },
    ],
  },
];

const componentIds = ['RCE', 'RCPRO', 'PJ', 'GOODS', 'GAREAT', 'CATNAT'];

const additionalQuoteComponentsSections = [
  {
    title: 'Frais grossistes',
    forTypes: ['WHOLESALE'],
    fields: [
      {
        name: 'wholesaleAdditionalCommissionPercentage',
        required: true,
        type: 'number',
        icon: Percent,
        apiDataAccessor: (data: PolicyDto) => {
          const wholesaleAdditionalCommissionAmountWithFeesPreTaxes = data?.quote?.components?.find(
            c => c.id === 'WHOLESALE_ADDITIONAL_COMMISSION',
          )?.premium.amountWithFeesPreTaxes;

          const premiumAmountPreFeesAndTaxes = data?.quote?.components
            ?.filter(c => componentIds.includes(c.id))
            ?.reduce((sum, c) => sum + (c.premium.amountPreFeesAndTaxes ?? 0), 0);

          if (
            !wholesaleAdditionalCommissionAmountWithFeesPreTaxes ||
            !premiumAmountPreFeesAndTaxes ||
            premiumAmountPreFeesAndTaxes === 0
          )
            return 10;

          return Math.round(
            (wholesaleAdditionalCommissionAmountWithFeesPreTaxes / premiumAmountPreFeesAndTaxes) *
              100,
          );
        },
        formDataAccessor: (data: Record<string, unknown>) => ({
          wholesaleAdditionalCommissionComponent: {
            componentIds,
            id: 'WHOLESALE_ADDITIONAL_COMMISSION',
            type: 'FEE',
            chargeType: 'RECURRING',
            amountPropertiesFrom: ['amountPreFeesAndTaxes'],
            percentage: Number(data.wholesaleAdditionalCommissionPercentage) ?? 0,
          } satisfies ManualQuotePercentageDto,
        }),
      },
      {
        name: 'wholesaleSubscriptionFee',
        required: false,
        type: 'number',
        icon: Euro,
        apiDataAccessor: (data: PolicyDto) => {
          const wholesaleSubscriptionFee = data?.quote?.components?.find(
            c => c.id === 'WHOLESALE_SUBSCRIPTION_FEE',
          );

          return centsToEuros(wholesaleSubscriptionFee?.premium?.amountWithFeesAndTaxes);
        },
        formDataAccessor: (data: Record<string, unknown>) => ({
          wholesaleSubscriptionFeeComponent: {
            id: 'WHOLESALE_SUBSCRIPTION_FEE',
            type: 'FEE',
            chargeType: 'ONE_TIME',
            premium: {
              amountPreFeesAndTaxes: 0,
              totalFees: eurosToCents(data.wholesaleSubscriptionFee as number | undefined) ?? 0,
              totalTaxes: 0,
            },
          } satisfies ManualQuoteComponentDto,
        }),
      },
    ],
  },
  {
    title: 'Frais distributeurs',
    forTypes: ['RETAIL'],
    fields: [
      {
        name: 'intermediaryCommission',
        required: false,
        type: 'number',
        icon: Percent,
        apiDataAccessor: (data: PolicyDto) => {
          const intermediaryCommission = data?.quote?.components?.find(
            c => c.id === 'RETAIL_COMMISSION',
          );

          const premiumAmountPreFeesAndTaxes = data?.quote?.components
            ?.filter(c => componentIds.includes(c.id))
            ?.reduce((sum, c) => sum + (c.premium.amountPreFeesAndTaxes ?? 0), 0);

          if (
            !intermediaryCommission ||
            premiumAmountPreFeesAndTaxes === undefined ||
            premiumAmountPreFeesAndTaxes === 0
          )
            return undefined;

          return Math.round(
            (intermediaryCommission.premium.amountWithFeesPreTaxes / premiumAmountPreFeesAndTaxes) *
              100,
          );
        },
        formDataAccessor: data => ({
          intermediaryCommissionPercentage: {
            componentIds,
            id: 'RETAIL_COMMISSION',
            type: 'FEE',
            chargeType: 'RECURRING',
            amountPropertiesFrom: ['amountPreFeesAndTaxes'],
            percentage: (data.intermediaryCommission ?? 0) as number,
          } satisfies ManualQuotePercentageDto,
          wholesaleAdditionalCommissionComponent: {
            componentIds,
            id: 'WHOLESALE_ADDITIONAL_COMMISSION',
            type: 'FEE',
            chargeType: 'RECURRING',
            amountPropertiesFrom: ['amountPreFeesAndTaxes'],
            percentage: 10,
          } satisfies ManualQuotePercentageDto,
        }),
      },
      {
        name: 'retailAnnualFee',
        required: false,
        type: 'number',
        icon: Euro,
        apiDataAccessor: (data: PolicyDto) => {
          const renewalFee = data?.quote?.components?.find(c => c.id === 'RETAIL_ANNUAL_FEE');
          return centsToEuros(renewalFee?.premium?.totalFees);
        },
        formDataAccessor: (data: Record<string, unknown>) => {
          const renewalFee = data.retailAnnualFee as number | undefined;
          if (!renewalFee) return {};
          return {
            renewalFeeComponent: {
              id: 'RETAIL_ANNUAL_FEE',
              type: 'FEE',
              chargeType: 'SPOT',
              premium: {
                amountPreFeesAndTaxes: 0,
                totalFees: eurosToCents(renewalFee) ?? 0,
                totalTaxes: 0,
              },
            } satisfies ManualQuoteComponentDto,
          };
        },
      },
      {
        name: 'retailSubscriptionFee',
        required: false,
        type: 'number',
        icon: Euro,
        apiDataAccessor: (data: PolicyDto) => {
          const retailSubscriptionFee = data?.quote?.components?.find(
            c => c.id === 'RETAIL_SUBSCRIPTION_FEE',
          );

          return centsToEuros(retailSubscriptionFee?.premium?.amountWithFeesAndTaxes);
        },
        formDataAccessor: (data: Record<string, unknown>) => {
          const totalFees = data.retailSubscriptionFee as number | undefined;

          if (!totalFees) return {};

          return {
            intermediarySubscriptionFeeComponent: {
              id: 'RETAIL_SUBSCRIPTION_FEE',
              type: 'FEE',
              chargeType: 'ONE_TIME',
              premium: {
                amountPreFeesAndTaxes: 0,
                totalFees: eurosToCents(totalFees) ?? 0,
                totalTaxes: 0,
              },
            } satisfies ManualQuoteComponentDto,
          };
        },
      },
    ],
  },
] as const satisfies Array<FormSection & { forTypes?: string[] }>;

const policyHolderInvitationRole = ['DRIVER'];

export const tutassurHiscoxResponsabiliteCivileProConfig = {
  display: {
    category: 'professionnel',
    icon: BriefcaseIcon,
    badges: ['online'],
  },
  productCustomPath: '/responsabilite-civile-pro-tarif-en-ligne',
  discoveryFunnelSteps: {
    afterEndPath: undefined,
    steps: [
      {
        path: 'documents-upload',
        type: 'documentUploadFunnel' as const,
        context: 'policy' as const,
        icon: FileUp,
      },
      {
        path: 'asset',
        type: 'emptyAssetFunnel',
        hidden: true,
      },
      {
        path: 'customer',
        type: 'customerFunnel',
        sections: companySections,
        icon: Building2,
      },
      {
        path: 'contact',
        type: 'customerFunnel',
        sections: signatorySections,
        icon: User,
      },
      {
        path: 'compliance',
        type: 'complianceFunnel',
        icon: BadgeCheck,
      },
      {
        path: 'billing',
        type: 'billingSelectionFunnel',
        icon: CalendarClock,
        frequencyIcons: BILLING_FREQUENCY_ICONS,
      },
      {
        path: 'policy',
        type: 'policyFunnel',
        sections: policySections,
        icon: ShieldAlert,
      },
      {
        path: 'quote',
        type: 'quoteFunnel',
        icon: Shield,
        policySections: [
          {
            title: 'Options des garanties',

            fields: [
              {
                name: 'companyRevenueOutsideUsaCanada',
                icon: Euro,
              },
              {
                name: 'companyRevenueInUSACanada',
                required: true,
                icon: Euro,
                showWhen: [
                  {
                    fieldName: 'peril-WHOLE_WORLD',
                    condition: 'equals',
                    value: 'true',
                  },
                ],
              },
            ],
          },
        ],
        assetSections: [
          {
            title: "Date de prise d'effet de l'assurance",
            fields: [
              {
                name: 'startedAt',
                type: 'datetime',
                min: 'now',
                icon: CalendarDays,
              },
            ],
          },
        ],
        additionalQuoteComponentsSections,
      },
      {
        path: 'end',
        type: 'funnelEnd',
        id: 'new-business-end',
        icon: PartyPopper,
        callback: {
          callToActionCallback: async ({ policyId, branchId }) => {
            await policyControllerPushPolicyEvent(
              policyId,
              { eventType: 'TUTASSUR_NB_QUOTE_EMITTED' },
              { branchId },
            );
          },
        },
        additionalComponents: {
          isQuoteTierModuleVisible: true,
        },
      },
    ],
  },
  mtaFunnelSteps: {
    afterEndPath: undefined,
    steps: [
      {
        path: 'new',
        type: 'newMTACreation',
        icon: FilePen,
      },
      {
        path: 'customer',
        type: 'customerFunnel',
        sections: companySections,
        icon: Building2,
      },
      {
        path: 'contact',
        type: 'customerFunnel',
        sections: signatorySections,
        icon: User,
      },
      {
        path: 'compliance',
        type: 'complianceFunnel',
        icon: BadgeCheck,
      },
      {
        path: 'policy',
        type: 'policyFunnel',
        icon: ShieldAlert,
        sections: [
          {
            title: 'Questions de souscription - Conditions générales',
            description: 'Ces questions sont obligatoires pour tous les contrats',
            fields: [
              { name: 'kqHiscoxHistory', display: 'toggle' as const, icon: AlertTriangle },
              { name: 'kqGeographicExposure', display: 'toggle' as const, icon: AlertTriangle },
              { name: 'kqClaimsHistory', display: 'toggle' as const, icon: AlertTriangle },
            ],
          },
          {
            title: 'Questions de souscription - Secteur Tech',
            showWhen: showForTECH,
            fields: [
              { name: 'kqActivityTECH', display: 'toggle' as const, icon: AlertTriangle },
              { name: 'kqPayrollPortage', display: 'toggle' as const, icon: AlertTriangle },
            ],
          },
          {
            title: 'Questions de souscription - Marketing & Communication',
            showWhen: showForMAC,
            fields: [
              { name: 'kqActivityMAC', display: 'toggle' as const, icon: AlertTriangle },
              { name: 'kqPayrollPortage', display: 'toggle' as const, icon: AlertTriangle },
            ],
          },
          {
            title: 'Questions de souscription - Médias',
            showWhen: showForMED,
            fields: [
              { name: 'kqActivityMED', display: 'toggle' as const, icon: AlertTriangle },
              { name: 'kqPayrollPortage', display: 'toggle' as const, icon: AlertTriangle },
            ],
          },
          {
            title: 'Questions de souscription - Management & Conseil',
            showWhen: showForMAN,
            fields: [
              { name: 'kqActivityMAN', display: 'toggle' as const, icon: AlertTriangle },
              { name: 'kqPayrollPortage', display: 'toggle' as const, icon: AlertTriangle },
            ],
          },
          {
            title: 'Questions de souscription - Activités diverses',
            showWhen: showForMISC,
            fields: [
              { name: 'kqActivityMISC', display: 'toggle' as const, icon: AlertTriangle },
              { name: 'kqPayrollPortage', display: 'toggle' as const, icon: AlertTriangle },
            ],
          },
          {
            title: 'Questions de souscription - Tatouage et Perçage',
            showWhen: showForTTP,
            fields: [
              { name: 'kqActivityTTP', display: 'toggle' as const, icon: AlertTriangle },
              { name: 'kqPayrollPortage', display: 'toggle' as const, icon: AlertTriangle },
            ],
          },
          {
            title: 'Questions de souscription - Bien-être',
            showWhen: showForBIE,
            fields: [
              { name: 'kqActivityBIE', display: 'toggle' as const, icon: AlertTriangle },
              { name: 'kqPayrollPortage', display: 'toggle' as const, icon: AlertTriangle },
            ],
          },
          {
            title: 'Questions de souscription - Tourisme et Voyages',
            showWhen: showForTAG,
            fields: [
              { name: 'kqActivityTAG', display: 'toggle' as const, icon: AlertTriangle },
              { name: 'kqPayrollPortage', display: 'toggle' as const, icon: AlertTriangle },
            ],
          },
          {
            title: 'Questions de souscription - Sécurité',
            showWhen: showForSECU,
            fields: [{ name: 'kqActivitySECU', display: 'toggle' as const, icon: AlertTriangle }],
          },
          {
            title: "Questions de souscription - Bureau d'Études Techniques",
            showWhen: showForBET,
            fields: [
              { name: 'kqActivityBET', display: 'toggle' as const, icon: AlertTriangle },
              { name: 'kqPayrollPortage', display: 'toggle' as const, icon: AlertTriangle },
            ],
          },
          {
            title: 'Questions de souscription - Artisanat, Commerce et Entreprise',
            showWhen: showForACE,
            fields: [
              { name: 'kqActivityACE', display: 'toggle' as const, icon: AlertTriangle },
              { name: 'kqPayrollPortage', display: 'toggle' as const, icon: AlertTriangle },
            ],
          },
        ],
      },
      {
        path: 'quote',
        type: 'quoteFunnel',
        icon: Shield,
        policySections: [],
        assetSections: [],
        additionalQuoteComponentsSections,
      },
      {
        path: 'end',
        type: 'funnelEnd',
        id: 'mta-end',
        icon: PartyPopper,
        callback: {},
        additionalComponents: {
          isQuoteTierModuleVisible: true,
        },
      },
    ],
  },
  policyInformationCard: {
    title: "Chiffre d'affaires annuel",
    fieldsConfig: ['companyRevenueOutsideUsaCanada', 'companyRevenueInUSACanada'],
  },
  customerInformations: {
    fieldsConfig: [
      { key: 'companyName', contactKey: ContactKey.COMPANY_NAME },
      'companyHeadquarterAddress',
      'companyHeadquarterPostcode',
      'companyHeadquarterCity',
      'companySiret',
      'companySiren',
      'companyNafCode',
      'companyActivity1',
      'companyActivityDistribution1',
      { key: 'companyActivity2', hideIfEmpty: true },
      {
        key: 'companyActivityDistribution2',
        hideIfEmpty: true,
        formatValue: value =>
          value && typeof value === 'number' && value > 0 ? `${value}%` : undefined,
      },
      { key: 'companyActivity3', hideIfEmpty: true },
      {
        key: 'companyActivityDistribution3',
        hideIfEmpty: true,
        formatValue: value =>
          value && typeof value === 'number' && value > 0 ? `${value}%` : undefined,
      },
      { key: 'companyActivity4', hideIfEmpty: true },
      {
        key: 'companyActivityDistribution4',
        hideIfEmpty: true,
        formatValue: value =>
          value && typeof value === 'number' && value > 0 ? `${value}%` : undefined,
      },
      { key: 'contactFirstName', contactKey: ContactKey.FIRST_NAME },
      { key: 'contactLastName', contactKey: ContactKey.LAST_NAME },
      'contactEmail',
      'contactPhoneNumber',
      'contactCivility',
      'contactBirthDate',
      'contactBirthCity',
      'contactBirthCountry',
    ],
  },
  quoteTierModuleConfig: {
    goToTiersOnMta:
      '/mta/responsabilite-civile-pro-tarif-en-ligne/${policy.customer}/${policy.id}/new',
    goToTiersOnNewBusiness:
      '/discovery/responsabilite-civile-pro-tarif-en-ligne/${policy.customer}/${policy.id}/${workingBranch?.id}/quote',
    fieldsConfig: [],
    perilsMapping: {},
    isExternalQuoteEnabled: false,
    isChosenTierDisplayed: true,
  },
  brokerPolicyDashboardHome: {
    isFinalizeNewBusinessModuleVisible: true,
    isAuthorCardModuleVisible: true,
    isPolicyholderInvitationModuleVisible: true,
    isQuoteTierModuleVisible: true,
    isPremiumModuleVisible: true,
    isPolicyLifecycleSummaryModuleVisible: true,
    isInvoicesBalanceModuleVisible: true,
    customModules: [],
  },
  additionalDocumentTypes: [
    'PSC_SPECIFIC_CONDITIONS',
    'ACTIVITY_SPECIFIC_CONDITIONS',
    'RCE_SPECIFIC_CONDITIONS',
    'TDMI_SPECIFIC_CONDITIONS',
    'GENERAL_QUOTE',
    'ACTIVITY_QUOTE',
    'RCE_QUOTE',
    'TDMI_QUOTE',
  ],
  policyholderInvitationModuleConfig: {
    relationTypes: policyHolderInvitationRole,
  },
  finalizeNewBusinessModuleConfig: {
    buttons: [
      {
        id: 'send-via-email',
        actionType: 'signature',
        visual: {
          label: 'Envoi du contrat au client',
          icon: 'mail',
          successMessage: 'Contrat envoyé par email',
        },
        signature: {
          signatureType: 'direct',
          deliveryMode: 'EMAIL',
          otp: {
            enabled: false,
          },
        },
        visibilityConditions: {
          policyStatuses: ['POLICY_QUOTED'],
        },
      },
    ],
    rules: [
      {
        id: 'documents-validation',
        link: ({ policyId, branchId }) => `/policies/${policyId}/${branchId}/documents`,
        isValid: ({ policy }) => policy.customFields.allClientDocumentsOk === true,
      },
      {
        id: 'sepa-payment',
        link: ({ policyId, branchId }) => `/policies/${policyId}/${branchId}/payment-methods`,
        isValid: ({ paymentMethods }) =>
          paymentMethods.some(({ paymentMethodType }) => paymentMethodType === 'sepa_debit'),
      },
    ],
  },
  priceBreakdownConfig: {
    groups: [
      { lines: [{ key: 'TOTAL_PREMIUM', variant: 'dimmed' }] },
      { lines: [{ key: 'BROKERAGE_FEES' }] },
      { lines: [{ key: 'SUBSCRIPTION_FEE' }, { key: 'ANNUAL_FEE', tooltip: true }] },
      { lines: [{ key: 'FGTI_TAX', tooltip: true }] },
    ],
  },
  policyDashboardEventsModuleConfig: {
    enableAssetComparison: false,
    enablePolicyComparison: true,
  },
} satisfies KorintModulesProductsConfig;
